import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Drawer } from '../../drawer/drawer'
import { getFacets } from '../audit-api'
import { AuditTimeline } from '../audit-timeline'
import { recordView } from '../entry-format'
import { EMPTY_FILTERS, filtersToParams, filtersToSearch } from '../filters-model'
import { useAuditCapabilities } from '../use-audit-capabilities'
import { catalogueTableOf, childLabelsOf, useAuditCatalogue } from '../use-audit-catalogue'
import '../audit.scss'
import { SEARCH_FIELD_PROPS } from '../../component/search-field-props'

const DEBOUNCE_MS = 300
const START = { ...EMPTY_FILTERS, range: 'all' }
// the filters a pivot or the search box narrows; "Clear filters" resets them
const NARROWING = ['q', 'topics', 'actors', 'apps', 'ip', 'request', 'session', 'within']

// "asset versions", "group memberships and roles": the catalogue's labels,
// lower-cased and in the plural, joined in plain English.
const plural = label => (/s$/i.test(label) ? label : `${label}s`)
export const childPhrase = labels => {
  const words = [...new Set(labels.filter(Boolean).map(l => plural(l.toLowerCase())))]
  if (words.length <= 1) return words[0] || ''
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

const sameRecord = (ref, rec) => Boolean(ref && ref.type === rec.type && String(ref.id) === String(rec.id))

// The record's name, as entry-format reads it: from the newest entry that
// names it (itself, or as the parent of a child's entry), else the page's
// own title, else the record content's first page names (page.record: a
// person's email when they have no entries), else "<label> <id>" from the
// catalogue.
const titleOf = (record, items, catalogue, pageRecord) => {
  for (const e of items || []) {
    if (sameRecord(e.record, record)) return recordView(e.record).text
    if (sameRecord(e.parent, record)) return recordView(e.parent).text
  }
  if (record.title) return record.title
  if (pageRecord && sameRecord(pageRecord, record) && (pageRecord.title || '').trim()) return recordView(pageRecord).text
  const t = catalogueTableOf(catalogue, record.type)
  return recordView({ type: record.type, id: record.id, label: t ? t.label : 'Record' }).text
}

// One record's History (spec §5; the mockup's drawer): its entries only,
// with the same display rules as the audit log (they come from
// entry-format), a search box, topic chips from the record's own facets,
// "Include views" and "Include its <children>". Nothing loads until it
// opens. Pivots to a request or a session open the audit log for readers
// who have it; any other pivot narrows the drawer.
//
// A node (record.tree) reads everything under it -- every node below it,
// deleted ones too, with their files and versions (content subtree=1) --
// and "This level only" reads the node and its own child rows (children=1).
// A section's top (record.root: Content, Templates, Library) reads the
// whole section, or with "This level only" its top-level items (owner
// 2026-10-08).
export const HistoryDrawer = ({ record, open, onClose }) => {
  const caps = useAuditCapabilities()
  const catalogue = useAuditCatalogue()
  const history = useHistory()
  const [filters, setFilters] = useState(START)
  const [text, setText] = useState('')
  const [kids, setKids] = useState(Boolean(record.children))
  const [thisLevel, setThisLevel] = useState(false)
  const [facets, setFacets] = useState(null)
  const [items, setItems] = useState([])
  const [pageRecord, setPageRecord] = useState(null)

  // the search box: 300 ms after the last keystroke
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => (f.q === text ? f : { ...f, q: text })), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  const tree = Boolean(record.tree || record.root)
  const childLabels = record.children && !tree ? childLabelsOf(catalogue, record.type) : []
  const hasKids = childLabels.length > 0
  const withKids = hasKids && kids

  const base = useMemo(() => {
    if (record.root) return thisLevel ? { root: record.root } : { root: record.root, subtree: '1' }
    const rec = `${record.type}:${record.id}`
    if (tree) return thisLevel ? { record: rec, children: '1' } : { record: rec, subtree: '1' }
    return { record: rec, children: withKids ? '1' : undefined }
  }, [record.root, record.type, record.id, tree, thisLevel, withKids])
  const searchLabel = record.root ? "Search this section's history" : "Search this record's history"
  const params = useMemo(() => ({ ...base, ...filtersToParams(filters) }), [base, filters])
  // the chips count the record's history without the topics picked, so
  // picking one keeps the others
  const facetKey = JSON.stringify({ ...base, ...filtersToParams({ ...filters, topics: [] }) })

  useEffect(() => {
    if (!open) return undefined
    let live = true
    getFacets(JSON.parse(facetKey)).then(
      f => live && setFacets(f || null),
      () => live && setFacets(null)
    )
    return () => {
      live = false
    }
  }, [open, facetKey])

  const sys = Boolean(caps.sysAuditLog)
  const onPivot = useCallback(({ withinName, ...patch }) => {
    if ((patch.request || patch.session) && caps.canAudit) {
      const base = caps.sysAuditLog && !caps.orgAuditLog ? '/admin/audit-log' : '/org/audit-log'
      onClose()
      history.push(`${base}${filtersToSearch(patch)}`)
      return
    }
    setFilters(f => ({ ...f, ...patch }))
  }, [caps.canAudit, caps.sysAuditLog, caps.orgAuditLog, history, onClose])

  const narrowed = NARROWING.some(k => (Array.isArray(filters[k]) ? filters[k].length : filters[k]))
  const clear = () => {
    setText('')
    setFilters(f => ({ ...f, ...Object.fromEntries(NARROWING.map(k => [k, EMPTY_FILTERS[k]])) }))
  }

  const topics = ((facets && facets.topics) || []).filter(t => t.count > 0 || filters.topics.includes(t.key))
  const toggleTopic = key => setFilters(f => ({ ...f, topics: f.topics.includes(key) ? f.topics.filter(t => t !== key) : [...f.topics, key] }))
  const onItems = useCallback((loaded, rec) => {
    setItems(loaded)
    setPageRecord(rec || null)
  }, [])
  const subtitle = titleOf(record, items, catalogue, pageRecord)

  return (
    <Drawer open={open} title="History" onClose={onClose} size="md">
      <div className="au-history">
        <p className="sw-hint m-0 au-history-of">{subtitle}</p>
        <input
          {...SEARCH_FIELD_PROPS}
          name="history-search"
          className="form-control form-control-sm"
          placeholder={searchLabel}
          aria-label={searchLabel}
          value={text}
          onChange={e => setText(e.target.value)}
        />
        {topics.length > 0 &&
          <div className="au-chips" role="group" aria-label="Topics">
            {topics.map(t =>
              <button key={t.key} type="button" className="btn sw-btn btn-sm" aria-pressed={filters.topics.includes(t.key)} onClick={() => toggleTopic(t.key)}>
                {t.label || t.key}
              </button>)}
          </div>}
        <div className="au-history-toggles">
          <div className="form-check form-switch m-0">
            <input id="au-history-views" className="form-check-input" type="checkbox" checked={filters.includeViews} onChange={e => setFilters(f => ({ ...f, includeViews: e.target.checked }))} />
            <label className="form-check-label" htmlFor="au-history-views">Include views</label>
          </div>
          {tree &&
            <div className="form-check form-switch m-0">
              <input id="au-history-level" className="form-check-input" type="checkbox" checked={thisLevel} onChange={e => setThisLevel(e.target.checked)} />
              <label className="form-check-label" htmlFor="au-history-level">This level only</label>
            </div>}
          {hasKids &&
            <div className="form-check form-switch m-0">
              <input id="au-history-kids" className="form-check-input" type="checkbox" checked={kids} onChange={e => setKids(e.target.checked)} />
              <label className="form-check-label" htmlFor="au-history-kids">{`Include its ${childPhrase(childLabels)}`}</label>
            </div>}
        </div>
        {narrowed && <button type="button" className="btn btn-sm sw-btn align-self-start" onClick={clear}>Clear filters</button>}
        <AuditTimeline
          params={params}
          enabled={open}
          context="history"
          historyOf={record}
          onPivot={onPivot}
          onItems={onItems}
          sys={sys}
          caps={caps}
          emptyText={narrowed ? 'Nothing in this history matches these filters.' : 'No history yet.'}
        />
      </div>
    </Drawer>
  )
}
